import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBasket, Timer } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { fmtGNF } from "@/lib/format";

export const pad = (n) => String(n).padStart(2, "0");

export function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = Math.max(0, new Date(target).getTime() - now);
  return {
    ms,
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms % 86400000) / 3600000),
    m: Math.floor((ms % 3600000) / 60000),
    s: Math.floor((ms % 60000) / 1000),
    done: ms <= 0,
  };
}

const CountBox = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="font-num grid min-w-[2.6rem] place-items-center rounded-lg bg-slate-900/80 px-2 py-1.5 text-lg font-bold text-white sm:text-xl">
      {value}
    </span>
    <span className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-rose-50/80">{label}</span>
  </div>
);

export const BigCountdown = ({ target }) => {
  const { d, h, m, s, done } = useCountdown(target);
  if (done) return <span className="font-num text-lg font-bold text-white">Offre terminée</span>;
  return (
    <div className="flex items-center gap-2" data-testid="deals-hero-countdown">
      {d > 0 && (
        <>
          <CountBox value={pad(d)} label="Jours" />
          <span className="text-xl font-bold text-white/50">:</span>
        </>
      )}
      <CountBox value={pad(h)} label="Heures" />
      <span className="text-xl font-bold text-white/50">:</span>
      <CountBox value={pad(m)} label="Min" />
      <span className="text-xl font-bold text-white/50">:</span>
      <CountBox value={pad(s)} label="Sec" />
    </div>
  );
};

export const DealCard = ({ product, onAdd }) => {
  const { d, h, m, s, done } = useCountdown(product.deal_ends_at);
  const out = (product.stock || 0) <= 0;
  const pct = product.compare_price > product.price ? Math.round((1 - product.price / product.compare_price) * 100) : 0;
  const img = mediaUrl((product.images || [])[0]);
  const tid = product.sku || product.id;
  return (
    <div className="ds-card group flex h-full flex-col overflow-hidden hover:-translate-y-0.5" data-testid={`deal-card-${tid}`}>
      <Link to={`/produit/${product.id}`} className="relative block aspect-square overflow-hidden bg-slate-100">
        {img ? (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full place-items-center text-slate-300">
            <ShoppingBasket className="h-10 w-10" />
          </div>
        )}
        {pct > 0 && (
          <span className="absolute left-2 top-2 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-extrabold text-white shadow-md" data-testid={`deal-discount-${tid}`}>
            -{pct}%
          </span>
        )}
        {out && (
          <div className="absolute inset-0 grid place-items-center bg-white/70 text-sm font-semibold text-slate-600">
            Rupture de stock
          </div>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <p className="text-[11px] font-medium uppercase tracking-wider text-brand">{product.category_name}</p>
        <Link
          to={`/produit/${product.id}`}
          className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-slate-900 hover:text-brand"
        >
          {product.name}
        </Link>
        <p className="font-num mt-2 text-base font-bold text-rose-600 sm:text-lg">{fmtGNF(product.price)}</p>
        {product.compare_price > product.price && (
          <p className="font-num text-xs text-slate-400 line-through">{fmtGNF(product.compare_price)}</p>
        )}
        <div
          className={`mt-2 inline-flex w-fit items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold ${
            done ? "bg-slate-100 text-slate-400" : "bg-rose-50 text-rose-700"
          }`}
          data-testid={`deal-countdown-${tid}`}
        >
          <Timer className="h-3.5 w-3.5" />
          {done ? "Offre terminée" : (
            <span className="font-num">
              {d > 0 ? `${d}j ` : ""}
              {pad(h)}:{pad(m)}:{pad(s)}
            </span>
          )}
        </div>
        <button
          type="button"
          disabled={out || done}
          onClick={() => onAdd(product)}
          className="ds-btn-primary mt-3 w-full text-xs sm:text-sm"
          data-testid={`deal-add-${tid}`}
        >
          <ShoppingBasket className="h-4 w-4" /> Ajouter
        </button>
      </div>
    </div>
  );
};
